import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { adminApi, customerApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatDate } from '../../lib/utils';
import { 
    Users, 
    Building, 
    Mail, 
    Calendar,
    CheckCircle,
    Trash2,
    Search,
    Plus,
    Edit,
    Globe,
    Target,
    X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';

const initialFormState = {
    email: '',
    password: '',
    name: '',
    company_name: '',
    website: '',
    company_summary: '',
    business_objectives: '',
    solutions: '',
    value_propositions: '',
    target_industries: '',
    target_company_sizes: '',
    target_geography: '',
    is_approved: true,
};

export default function AdminCustomersPage() {
    const [loading, setLoading] = useState(true);
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(initialFormState);

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        try {
            const response = await adminApi.getCustomers();
            setCustomers(response.data);
        } catch (error) {
            toast.error('Failed to load customers');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setEditingCustomer(null);
        setForm(initialFormState);
        setDialogOpen(true);
    };

    const handleOpenEdit = (customer) => {
        setEditingCustomer(customer);
        setForm({
            email: customer.user.email || '',
            password: '',
            name: customer.user.name || '',
            company_name: customer.details?.company_name || customer.user.company_name || '',
            website: customer.details?.website || '',
            company_summary: customer.details?.company_summary || '',
            business_objectives: customer.details?.business_objectives || '',
            solutions: (customer.details?.solutions || []).join('\n'),
            value_propositions: (customer.details?.value_propositions || []).join('\n'),
            target_industries: (customer.details?.target_industries || []).join('\n'),
            target_company_sizes: (customer.details?.target_company_sizes || []).join('\n'),
            target_geography: (customer.details?.target_geography || []).join('\n'),
            is_approved: customer.details?.is_approved || false,
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.email || !form.company_name) {
            toast.error('Email and Company Name are required');
            return;
        }
        if (!editingCustomer && !form.password) {
            toast.error('Password is required for new customers');
            return;
        }

        setSaving(true);
        try {
            const data = {
                email: form.email,
                password: form.password || 'unchanged',
                name: form.name,
                company_name: form.company_name,
                website: form.website,
                company_summary: form.company_summary,
                business_objectives: form.business_objectives,
                solutions: form.solutions.split('\n').filter(s => s.trim()),
                value_propositions: form.value_propositions.split('\n').filter(s => s.trim()),
                target_industries: form.target_industries.split('\n').filter(s => s.trim()),
                target_company_sizes: form.target_company_sizes.split('\n').filter(s => s.trim()),
                target_geography: form.target_geography.split('\n').filter(s => s.trim()),
                is_approved: form.is_approved,
            };

            if (editingCustomer) {
                await adminApi.updateCustomer(editingCustomer.user.id, data);
                toast.success('Customer updated successfully');
            } else {
                await adminApi.createCustomer(data);
                toast.success('Customer created successfully');
            }

            setDialogOpen(false);
            loadCustomers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save customer');
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (customerId) => {
        try {
            await customerApi.approve(customerId);
            toast.success('Customer approved');
            loadCustomers();
        } catch (error) {
            toast.error('Failed to approve customer');
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this customer?')) return;
        
        try {
            await adminApi.deleteCustomer(userId);
            toast.success('Customer deleted');
            loadCustomers();
        } catch (error) {
            toast.error('Failed to delete customer');
        }
    };

    const updateForm = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const filteredCustomers = customers.filter(c => 
        c.user.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.user.email?.toLowerCase().includes(search.toLowerCase()) ||
        c.details?.company_name?.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="admin-customers-page">
            <Header 
                title="Customer Management"
                subtitle="View, create and manage all customer accounts"
            />

            <div className="p-8">
                {/* Search and Actions */}
                <div className="flex items-center justify-between gap-4 mb-8">
                    <div className="flex items-center gap-4">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-content-muted" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search customers..."
                                className="input-base pl-10 w-64"
                                data-testid="search-customers-input"
                            />
                        </div>
                        <div className="flex items-center gap-2 text-sm text-content-secondary">
                            <Users className="w-4 h-4" />
                            {filteredCustomers.length} customer{filteredCustomers.length !== 1 ? 's' : ''}
                        </div>
                    </div>
                    <Button onClick={handleOpenCreate} className="btn-primary gap-2" data-testid="add-customer-btn">
                        <Plus className="w-4 h-4" />
                        ADD CUSTOMER
                    </Button>
                </div>

                {/* Customer Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                    {filteredCustomers.map((customer) => (
                        <div 
                            key={customer.user.id}
                            className="card-base p-6"
                            data-testid={`customer-card-${customer.user.id}`}
                        >
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-lg bg-brand-red/10 flex items-center justify-center">
                                        <Building className="w-6 h-6 text-brand-red" />
                                    </div>
                                    <div>
                                        <h3 className="font-medium text-white">
                                            {customer.details?.company_name || customer.user.company_name || 'No Company'}
                                        </h3>
                                        <p className="text-sm text-content-muted">{customer.user.name}</p>
                                    </div>
                                </div>
                                {customer.details?.is_approved ? (
                                    <Badge className="badge badge-success">Approved</Badge>
                                ) : (
                                    <Badge className="badge badge-warning">Pending</Badge>
                                )}
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-center gap-2 text-sm">
                                    <Mail className="w-4 h-4 text-content-muted" />
                                    <span className="text-content-secondary">{customer.user.email}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <Calendar className="w-4 h-4 text-content-muted" />
                                    <span className="text-content-secondary">
                                        Joined {formatDate(customer.user.created_at)}
                                    </span>
                                </div>
                                {customer.details?.website && (
                                    <div className="flex items-center gap-2 text-sm">
                                        <Globe className="w-4 h-4 text-content-muted" />
                                        <a 
                                            href={customer.details.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-brand-red hover:underline truncate"
                                        >
                                            {customer.details.website}
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Target Info Summary */}
                            {customer.details && (
                                <div className="mb-6 p-3 rounded-lg bg-surface-app">
                                    <div className="grid grid-cols-3 gap-3 text-center">
                                        <div>
                                            <p className="text-lg font-bold text-white">
                                                {customer.details.target_industries?.length || 0}
                                            </p>
                                            <p className="text-xs text-content-muted">Industries</p>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-white">
                                                {customer.details.target_geography?.length || 0}
                                            </p>
                                            <p className="text-xs text-content-muted">Regions</p>
                                        </div>
                                        <div>
                                            <p className="text-lg font-bold text-white">
                                                {customer.details.solutions?.length || 0}
                                            </p>
                                            <p className="text-xs text-content-muted">Solutions</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="flex gap-2">
                                <Button
                                    variant="ghost"
                                    className="btn-ghost flex-1 gap-2"
                                    onClick={() => handleOpenEdit(customer)}
                                >
                                    <Edit className="w-4 h-4" />
                                    Edit
                                </Button>
                                {!customer.details?.is_approved && customer.details && (
                                    <Button
                                        onClick={() => handleApprove(customer.details.id)}
                                        className="btn-primary flex-1 gap-2"
                                        data-testid={`approve-${customer.user.id}`}
                                    >
                                        <CheckCircle className="w-4 h-4" />
                                        Approve
                                    </Button>
                                )}
                                <Button
                                    variant="ghost"
                                    className="btn-ghost text-status-error hover:text-status-error"
                                    onClick={() => handleDelete(customer.user.id)}
                                    data-testid={`delete-${customer.user.id}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>

                {filteredCustomers.length === 0 && (
                    <div className="text-center py-16">
                        <Users className="w-16 h-16 text-content-muted mx-auto mb-4" />
                        <h3 className="text-lg font-heading font-bold text-white mb-2">
                            No Customers Found
                        </h3>
                        <p className="text-content-secondary mb-4">
                            {search ? 'Try a different search term' : 'No customers have been created yet'}
                        </p>
                        <Button onClick={handleOpenCreate} className="btn-primary gap-2">
                            <Plus className="w-4 h-4" />
                            Add Your First Customer
                        </Button>
                    </div>
                )}
            </div>

            {/* Create/Edit Customer Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-surface-card max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white font-heading">
                            {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                        </DialogTitle>
                    </DialogHeader>

                    <Tabs defaultValue="account" className="mt-4">
                        <TabsList className="bg-surface-app flex-wrap h-auto gap-1 p-1">
                            <TabsTrigger value="account" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                                Account
                            </TabsTrigger>
                            <TabsTrigger value="company" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                                Company
                            </TabsTrigger>
                            <TabsTrigger value="solutions" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                                Solutions
                            </TabsTrigger>
                            <TabsTrigger value="targeting" className="data-[state=active]:bg-brand-red data-[state=active]:text-white">
                                Targeting
                            </TabsTrigger>
                        </TabsList>

                        {/* Account Tab */}
                        <TabsContent value="account" className="mt-4 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Email *</Label>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) => updateForm('email', e.target.value)}
                                        className="input-base"
                                        placeholder="customer@company.com"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">
                                        Password {editingCustomer ? '(leave blank to keep current)' : '*'}
                                    </Label>
                                    <Input
                                        type="password"
                                        value={form.password}
                                        onChange={(e) => updateForm('password', e.target.value)}
                                        className="input-base"
                                        placeholder={editingCustomer ? '••••••••' : 'Enter password'}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Contact Name</Label>
                                    <Input
                                        value={form.name}
                                        onChange={(e) => updateForm('name', e.target.value)}
                                        className="input-base"
                                        placeholder="John Doe"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Company Name *</Label>
                                    <Input
                                        value={form.company_name}
                                        onChange={(e) => updateForm('company_name', e.target.value)}
                                        className="input-base"
                                        placeholder="Acme Corporation"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center gap-3 p-4 rounded-lg bg-surface-app">
                                <Checkbox
                                    id="is_approved"
                                    checked={form.is_approved}
                                    onCheckedChange={(checked) => updateForm('is_approved', checked)}
                                    className="data-[state=checked]:bg-status-success data-[state=checked]:border-status-success"
                                />
                                <div>
                                    <Label htmlFor="is_approved" className="text-white cursor-pointer">
                                        Approve Customer Immediately
                                    </Label>
                                    <p className="text-xs text-content-muted">
                                        Customer will have full access to their dashboard
                                    </p>
                                </div>
                            </div>
                        </TabsContent>

                        {/* Company Tab */}
                        <TabsContent value="company" className="mt-4 space-y-4">
                            <div className="space-y-2">
                                <Label className="text-content-secondary">Website</Label>
                                <Input
                                    value={form.website}
                                    onChange={(e) => updateForm('website', e.target.value)}
                                    className="input-base"
                                    placeholder="https://www.company.com"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-content-secondary">Company Summary</Label>
                                <Textarea
                                    value={form.company_summary}
                                    onChange={(e) => updateForm('company_summary', e.target.value)}
                                    className="input-base min-h-[100px]"
                                    placeholder="Brief description of the company..."
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-content-secondary">Business Objectives for ABM</Label>
                                <Textarea
                                    value={form.business_objectives}
                                    onChange={(e) => updateForm('business_objectives', e.target.value)}
                                    className="input-base min-h-[100px]"
                                    placeholder="What are the key objectives for ABM campaigns..."
                                />
                            </div>
                        </TabsContent>

                        {/* Solutions Tab */}
                        <TabsContent value="solutions" className="mt-4 space-y-4">
                            <div className="space-y-2">
                                <Label className="text-content-secondary">Solutions Being Positioned (one per line)</Label>
                                <Textarea
                                    value={form.solutions}
                                    onChange={(e) => updateForm('solutions', e.target.value)}
                                    className="input-base min-h-[120px]"
                                    placeholder="Enterprise SaaS Platform&#10;Data Analytics Suite&#10;Cloud Infrastructure"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-content-secondary">Key Value Propositions (one per line)</Label>
                                <Textarea
                                    value={form.value_propositions}
                                    onChange={(e) => updateForm('value_propositions', e.target.value)}
                                    className="input-base min-h-[120px]"
                                    placeholder="Reduce operational costs by 40%&#10;Increase productivity by 2x&#10;Enterprise-grade security"
                                />
                            </div>
                        </TabsContent>

                        {/* Targeting Tab */}
                        <TabsContent value="targeting" className="mt-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Target Industries (one per line)</Label>
                                    <Textarea
                                        value={form.target_industries}
                                        onChange={(e) => updateForm('target_industries', e.target.value)}
                                        className="input-base min-h-[150px]"
                                        placeholder="Financial Services&#10;Healthcare&#10;Manufacturing&#10;Technology"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Target Company Sizes (one per line)</Label>
                                    <Textarea
                                        value={form.target_company_sizes}
                                        onChange={(e) => updateForm('target_company_sizes', e.target.value)}
                                        className="input-base min-h-[150px]"
                                        placeholder="Enterprise (1000+)&#10;Mid-Market (200-1000)&#10;SMB (50-200)"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-content-secondary">Target Geography (one per line)</Label>
                                    <Textarea
                                        value={form.target_geography}
                                        onChange={(e) => updateForm('target_geography', e.target.value)}
                                        className="input-base min-h-[150px]"
                                        placeholder="United States&#10;Canada&#10;United Kingdom&#10;APAC"
                                    />
                                </div>
                            </div>
                        </TabsContent>
                    </Tabs>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-line-default">
                        <Button variant="ghost" onClick={() => setDialogOpen(false)} className="btn-ghost">
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={saving} className="btn-primary">
                            {saving ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Create Customer'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
